import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { useCatalog } from '../../contexts/CatalogContext';
import { usePages } from '../../contexts/SiteContext';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';

/**
 * Reviews als één stem tegelijk: een groot aanhalingsteken als beeld, de quote groot gezet,
 * en eronder de namen. Kies een naam en de quote wisselt. Geen automatisch doordraaien:
 * je leest in je eigen tempo. Zonder echte reviews de kop uit Paginateksten.
 */
export function HomeReviews() {
  const pages = usePages();
  const {
    shopRating
  } = useCatalog();
  const reduce = useReducedMotion();
  const copy = pages.home.reviews;
  const quotes = pages.testimonials;
  const [index, setIndex] = useState(0);
  const count = shopRating.count;
  const avg = rating(shopRating.average);
  const current = quotes[Math.min(index, quotes.length - 1)];
  return <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="home-reviews" data-id="element-7452" data-mp="2uyiniue8i8iu">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7453" data-mp="2gyho7jdmb95h">
        <div className="lg:col-span-4" data-id="element-7454" data-mp="2lx2mhd9b7ira">
          {count > 0 ? <>
              {/* De sterren zijn beeld; de kop eronder zegt hetzelfde in woorden. */}
              <p className="flex items-center gap-1" aria-hidden="true" data-id="element-7455" data-mp="2c0pxutsmfv3e">
                {Array.from({
              length: 5
            }, (_, i) => <StarIcon key={i} className="h-4 w-4 fill-current text-clay" strokeWidth={0} data-id="element-7456" data-mp="1ya2zj2qrw77u" />)}
              </p>
              <h2 id="home-reviews" className="mt-4 font-display text-4xl font-light leading-[1.08] text-ink" data-id="element-7457" data-mp="235i7je23n8wc">
                {avg} uit {plural(count, 'beoordeling', 'beoordelingen')}
                <span className="sr-only" data-id="element-7458" data-mp="1t8x612pagfx9"> (van 5 sterren)</span>
              </h2>
            </> : <h2 id="home-reviews" className="font-display text-4xl font-light leading-[1.08] text-ink" data-id="element-7459" data-mp="1a6wm6smkaof5">
              <HeadingText heading={copy.heading} data-id="element-7460" data-mp="15cio2igim36s" />
            </h2>}
          {copy.body && <p className="mt-4 max-w-sm text-[16px] leading-relaxed text-muted" data-id="element-7461" data-mp="10cxlly24zcpb">{fill(copy.body)}</p>}
        </div>

        {current && <div className="lg:col-span-8" data-id="element-7462" data-mp="1t47iq5gt73qw">
            {/* Het aanhalingsteken als beeld; in de flow, zodat het nooit over de kop erboven valt. */}
            <span aria-hidden="true" className="block h-12 select-none font-display text-[7rem] font-light leading-[0.9] text-clay sm:h-16 sm:text-[9rem]" data-id="element-7463" data-mp="1o2ry2eijmz91">
              “
            </span>
            <div aria-live="polite" className="min-h-[13rem] sm:min-h-[15rem]" data-id="element-7464" data-mp="1e6f5xl2nctge">
              <AnimatePresence mode="wait" initial={false} data-id="element-7465" data-mp="7re634ai3r4j">
                <motion.figure key={index} initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease
            }} data-id="element-7466" data-mp="2rrsuuuoaavq">
                  <blockquote className="font-display text-[1.9rem] font-light leading-[1.22] tracking-[-0.01em] text-ink sm:text-[2.5rem] lg:text-[2.8rem]" data-id="element-7467" data-mp="2odb1g6afe8vg">
                    {fill(current.quote)}
                  </blockquote>
                  <figcaption className="mt-6 text-[14px] text-muted" data-id="element-7468" data-mp="2y22rg8vxej3q">
                    {current.name} · {current.context}
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </div>

            {quotes.length > 1 && <ul aria-label="Kies een review" className="mt-10 flex flex-wrap gap-x-6 gap-y-2 border-t border-ink/15 pt-5" data-id="element-7469" data-mp="3c35fgr6he3ha">
                {quotes.map((t, i) => {
            const isActive = i === index;
            return <li key={`${t.name}-${t.context}`} data-id="element-7470" data-mp="1hous6n5jbhdm" data-mpi={i}>
                      <button type="button" aria-pressed={isActive} onClick={() => setIndex(i)} className={`relative min-h-[44px] whitespace-nowrap text-[15px] transition-colors duration-150 ease-soft ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-7471" data-mp="384x0ib9a439w">
                        {t.name}
                        {isActive && <motion.span layoutId="review-marker" aria-hidden="true" className="absolute inset-x-0 -top-[21px] h-px bg-ink" transition={{
                  duration: reduce ? 0 : 0.25,
                  ease
                }} data-id="element-7472" data-mp="2pd48ok5y4o3r" />}
                      </button>
                    </li>;
          })}
              </ul>}
          </div>}
      </div>
    </section>;
}